import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, createElementVNode as _createElementVNode, Fragment as _Fragment, createElementBlock as _createElementBlock } from "vue"

import { reactive, ref, computed, PropType, onMounted, nextTick } from 'vue'
import { useCmTableListRefresh } from '@/hooks/use-cm-table-list-refresh'
import { useI18n } from 'vue-i18n'
import CmTable from '@/components/cmTable/index.vue'
import { listCol } from '@/utils/list-columns-i18n'

import SearchFilterBar from '@/components/searchFilterBar/index.vue'
import TerminalIconName from '@/views/dashboard/_components/terminal-icon-name/terminal-icon-name.vue'
import IPColumn from '@/views/dashboard/_components/ip-column/IPColumn.vue'
import {
  TerminalViewService,
  QueryParams as TerminalQueryParams,
  TerminalItem,
} from '../terminal-view/terminal-view.service'
import PortListDrawer from '../terminal-view/_components/port-list-drawer/port-list-drawer.vue'
import BlockRuleListDrawer from '../terminal-view/_components/block-rule-list-drawer/block-rule-list-drawer.vue'
import { LinuxDriverSwitch } from '@/config/switch'
import { useReflectiveInjector } from '@tanbo/vue-di-plugin'
import {
  ActionType,
  FormSize,
  FormType,
  ObjectFormItem,
} from '@/components/cmForm/types'
import { TableOpt, TableActOpt } from '@/components/cmTable/types'
import { TableViewMode, TableViewModeType } from '@/views/dashboard/_enum/table-view-mode'
import { TerminalTypeOptions } from '@/views/dashboard/_enum/terminal-type'
import { ServicePermissionService, PermissionFe } from '@/services/service-permission.service'

type FilterFormParams = Extract<keyof TerminalQueryParams, 'system_type'>

export default /*#__PURE__*/_defineComponent({
  __name: 'terminal-list-top10',
  props: {
  viewMode: {
    type: String as PropType<TableViewModeType>,
    default: TableViewMode.preview
  },
},
  emits: ['viewAll'],
  setup(__props, { expose: __expose, emit: __emit }) {

const { t, locale } = useI18n()
const props = __props

const emits = __emit

const injector = useReflectiveInjector()
const terminalViewService = injector.get(TerminalViewService)
const servicePermissionService = injector.get(ServicePermissionService)
const dataApi = terminalViewService.getTerminalList.bind(terminalViewService)
const hasPermission = servicePermissionService.getHasPermission(PermissionFe.port_control)

const tableRef = ref()

const { refreshList } = useCmTableListRefresh(tableRef)
const portListDrawer = ref(false)
const blockRuleListDrawer = ref(false)
const currentDevice = ref<TerminalItem>({} as TerminalItem)
const riskType = ref<number | undefined>(undefined)

/** 与 CmTable v-model、SearchFilterBar v-model 共用，避免再传 queryParams 导致分页/筛选双份状态 */
const form = reactive<Partial<TerminalQueryParams>>({
  page: 1,
  limit: props.viewMode === TableViewMode.preview ? 10 : 20,
  key_word: '',
  range: 1,
  system_type: undefined,
})

const filterFormColumns = computed<ObjectFormItem<TerminalQueryParams, FilterFormParams>>(() => {
  void locale.value
  return {
    system_type: {
      colOpt: { style: 'width: 150px;flex: 0 0 auto' },
      label: t('portManage.systemType'),
      type: FormType.Select,
      dictData: TerminalTypeOptions,
      outSearchFiledFlag: true,
      selectOpt: {
        clearable: true,
      },
      hiddenAllLabel: true,
      events: {
        change: () => {
          handleQuery()
        },
      }
    },
  }
})

const tableActOpt = computed<TableActOpt>(() => {
  void locale.value
  return {
    columnOpt: { label: listCol(t, 'operation'), width: 100, align: 'left', fixed: 'right' },
    actionButtons: () => [],
  }
})

const tableOpt = computed<TableOpt>(() => {
  void locale.value
  return {
    bodyStyle: props.viewMode === TableViewMode.full ? 'padding: 10px 16px' : 'background: transparent;padding:0',
    containerOpt: { style: 'padding: 0;background: var(--cm-color-card-bg)' },
    path: 'list',
    height: props.viewMode === TableViewMode.preview ? undefined : '100%',
    showActionBar: false,
    showForm: false,
    showRightToolbar: false,
    rowKey: 'device_id',
    selection: false,
    showTip: false,
    usePagination: false,
    disableScrollLoad: props.viewMode === TableViewMode.preview,
    scrollLoadThreshold: 10,
    scrollLoadDelay: 100,
    columns: [
      {
        label: listCol(t, 'computerName'),
        prop: 'computer_name',
        type:"COMPUTER",
        sortable: true,
      },
      {
        label: listCol(t, 'userMark'),
        prop: 'user_mark',
      },
      {
        label: t('portManage.osPlaceholder'),
        prop: 'system_name',
        sortable: true,
      },
      {
        label: listCol(t, 'ipAddress'),
        prop: 'ip_address',
        type:"IP",
      },
      {
        label: t('portManage.riskPortCount'),
        prop: 'risk_count',
        sortable: true,
      },
      {
        label: t('portManage.unknownPortCount'),
        prop: 'unknown_count',
        sortable: true,
      },
      {
        label: t('portManage.blockRuleCount'),
        prop: 'rule_count',
        sortable: true,
      },
    ],
  }
})

async function handleSearch(value: string) {
  await nextTick()
  form.key_word = value
  void refreshList()
}

async function handleQuery() {
  await refreshList()
}

function handleReset() {
  form.key_word = ''
  form.system_type = 0
  void refreshList()
}

function handleAction({ type }: { type: any }) {
  // Handle actions
}

function handleViewPortList(device: TerminalItem, type: number) {
  riskType.value = type
  currentDevice.value = { ...device }
  portListDrawer.value = true
}

function handlePortListDrawerClose() {
  portListDrawer.value = false
}

function handleViewBlockRuleList(device: TerminalItem) {
  currentDevice.value = { ...device }
  blockRuleListDrawer.value = true
}

function handleBlockRuleListDrawerClose() {
  blockRuleListDrawer.value = false
}

function handleDrawerRefresh() {
  void refreshList()
}

onMounted(async () => {
  if (!hasPermission) return
  await refreshList()
})

__expose({
  tableRef,
  getList: () => void refreshList(),
})

return (_ctx: any,_cache: any) => {
  const _component_el_button = _resolveComponent("el-button")!
  const _component_el_popover = _resolveComponent("el-popover")!
  const _component_el_drawer = _resolveComponent("el-drawer")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(CmTable, {
      ref_key: "tableRef",
      ref: tableRef,
      modelValue: form,
      "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((form) = $event)),
      tableOpt: tableOpt.value,
      tableActOpt: tableActOpt.value,
      firstLoading: false,
      "has-permission": _unref(hasPermission),
      api: _unref(dataApi),
      pageOption: {
      style: 'padding-bottom: 16px',
      pageNumName: 'page',
      pageSizeName: 'limit',
    },
      onHandleAction: handleAction
    }, {
      header: _withCtx(() => [
        _createVNode(SearchFilterBar, {
          modelValue: form,
          "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((form) = $event)),
          "form-columns": filterFormColumns.value,
          disabled: !_unref(hasPermission),
          "show-search": true,
          "search-placeholder": _ctx.$t('portManage.computerNameIp'),
          "popover-threshold": 2,
          onSearch: handleSearch,
          onQuery: handleQuery,
          onReset: handleReset
        }, {
          actions: _withCtx(() => [
            (props.viewMode === _unref(TableViewMode).preview)
              ? (_openBlock(), _createBlock(_component_el_button, {
                  key: 0,
                  size: "small",
                  disabled: !_unref(hasPermission),
                  onClick: _cache[0] || (_cache[0] = ($event: any) => (_ctx.$emit('viewAll')))
                }, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(_ctx.$t('commonPage.viewAll')), 1)
                  ]),
                  _: 1
                }, 8, ["disabled"]))
              : _createCommentVNode("", true)
          ]),
          _: 1
        }, 8, ["modelValue", "form-columns", "disabled", "search-placeholder"])
      ]),
      risk_count: _withCtx((slotProps) => [
        _createVNode(_component_el_popover, {
          placement: "bottom-start",
          trigger: "hover",
          content: _unref(LinuxDriverSwitch).networkCloseTip,
          disabled: !((slotProps as any).row.system_type === 2 && !_unref(LinuxDriverSwitch).network)
        }, {
          reference: _withCtx(() => [
            _createElementVNode("span", null, [
              _createVNode(_component_el_button, {
                type: "text",
                onClick: ($event: any) => (handleViewPortList((slotProps as any).row, 1)),
                disabled: (slotProps as any).row.system_type === 2 && !_unref(LinuxDriverSwitch).network
              }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString((slotProps as any).row.risk_count || 0), 1)
                ]),
                _: 2
              }, 1032, ["onClick", "disabled"])
            ])
          ]),
          _: 2
        }, 1032, ["content", "disabled"])
      ]),
      unknown_count: _withCtx((slotProps) => [
        _createVNode(_component_el_popover, {
          placement: "bottom-start",
          trigger: "hover",
          content: _unref(LinuxDriverSwitch).networkCloseTip,
          disabled: !((slotProps as any).row.system_type === 2 && !_unref(LinuxDriverSwitch).network)
        }, {
          reference: _withCtx(() => [
            _createElementVNode("span", null, [
              _createVNode(_component_el_button, {
                type: "text",
                onClick: ($event: any) => (handleViewPortList((slotProps as any).row, 2)),
                disabled: (slotProps as any).row.system_type === 2 && !_unref(LinuxDriverSwitch).network
              }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString((slotProps as any).row.unknown_count || 0), 1)
                ]),
                _: 2
              }, 1032, ["onClick", "disabled"])
            ])
          ]),
          _: 2
        }, 1032, ["content", "disabled"])
      ]),
      rule_count: _withCtx((slotProps) => [
        _createVNode(_component_el_popover, {
          placement: "bottom-start",
          trigger: "hover",
          content: _unref(LinuxDriverSwitch).networkCloseTip,
          disabled: !((slotProps as any).row.system_type === 2 && !_unref(LinuxDriverSwitch).network)
        }, {
          reference: _withCtx(() => [
            _createElementVNode("span", null, [
              _createVNode(_component_el_button, {
                type: "text",
                onClick: ($event: any) => (handleViewBlockRuleList((slotProps as any).row)),
                disabled: (slotProps as any).row.system_type === 2 && !_unref(LinuxDriverSwitch).network
              }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString((slotProps as any).row.rule_count), 1)
                ]),
                _: 2
              }, 1032, ["onClick", "disabled"])
            ])
          ]),
          _: 2
        }, 1032, ["content", "disabled"])
      ]),
      _: 1
    }, 8, ["modelValue", "tableOpt", "tableActOpt", "has-permission", "api"]),
    _createVNode(_component_el_drawer, {
      size: "60%",
      modelValue: portListDrawer.value,
      "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((portListDrawer).value = $event)),
      direction: "rtl",
      title: _ctx.$t('portManage.blockedTerminal'),
      "before-close": handlePortListDrawerClose,
      "destroy-on-close": ""
    }, {
      default: _withCtx(() => [
        (currentDevice.value.device_id)
          ? (_openBlock(), _createBlock(PortListDrawer, {
              key: 0,
              device: currentDevice.value,
              "risk-type": riskType.value,
              onRefresh: handleDrawerRefresh
            }, null, 8, ["device", "risk-type"]))
          : _createCommentVNode("", true)
      ]),
      _: 1
    }, 8, ["modelValue", "title"]),
    _createVNode(_component_el_drawer, {
      size: "60%",
      modelValue: blockRuleListDrawer.value,
      "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((blockRuleListDrawer).value = $event)),
      direction: "rtl",
      title: _ctx.$t('portManage.blockRule'),
      "before-close": handleBlockRuleListDrawerClose,
      "destroy-on-close": ""
    }, {
      default: _withCtx(() => [
        (currentDevice.value.device_id)
          ? (_openBlock(), _createBlock(BlockRuleListDrawer, {
              key: 0,
              device: currentDevice.value,
              onRefresh: handleDrawerRefresh
            }, null, 8, ["device"]))
          : _createCommentVNode("", true)
      ]),
      _: 1
    }, 8, ["modelValue", "title"])
  ], 64))
}
}

})